import React, { useState } from 'react';
import {
  IAuditoriaAssetRecoveryResultado,
  IAuditoriaAssetRecoveryItem,
} from '../interfaces/IAuditoriaAssetRecovery';
import { formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAssetRecoveryResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value?: number) =>
  `${(value || 0).toFixed(4)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-info';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'OMISSAO_RECEITA')
    return <span className='badge badge-light-danger'>Omissão de Receita</span>;
  if (tipo === 'SEM_CONTRAPARTIDA')
    return <span className='badge badge-light-warning'>Sem Contrapartida</span>;
  return <span className='badge badge-light-info'>Reversão Parcial</span>;
};

// COR-286: drill-down a nível de lançamento — todo AVISO/ALERTA/ERRO precisa mostrar não só a
// divergência agregada do mês, mas os lançamentos reais que a compõem.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IAuditoriaAssetRecoveryItem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const ItemRow: React.FC<{ item: IAuditoriaAssetRecoveryItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.mesAno}</span>
        </td>
        <td className='text-end'>{formatCurrency(item.creditoBaixaEstoques)}</td>
        <td className='text-end'>{formatCurrency(item.creditoPDD)}</td>
        <td className='text-end fw-bold'>{formatCurrency(item.creditoDespesaTotal)}</td>
        <td className='text-end'>{formatCurrency(item.receitaReversao)}</td>
        <td className='text-end fw-bolder text-danger'>{formatCurrency(item.divergencia)}</td>
        <td className='text-end'>
          <div className='fw-bold text-danger'>{formatCurrency(item.passivoPresumido)}</div>
          <div className='text-muted fs-9'>
            Multa: {formatCurrency(item.multaLei8218)} | Trib: {formatCurrency(item.tributosPresumidos)}
          </div>
        </td>
        <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={9} className='bg-light'>
            <div className='p-4'>
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Receita Bruta do Mês</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.receitaBrutaMensal)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Divergência</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.divergencia)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>% Divergência / RB</div>
                    <div className='fw-bolder fs-5'>{formatPercent(item.percentualDivergencia)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Passivo Presumido</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.passivoPresumido)}</div>
                  </div>
                </div>
              </div>

              {(item.status === 'ERRO' || item.status === 'ALERTA') && (
                <div className='row g-3 mb-3'>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Multa ECD Potencial (Lei 8.218/1991)</div>
                      <div className='fw-bolder fs-5 text-warning'>{formatCurrency(item.multaLei8218)}</div>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Tributos Presumidos IRPJ+CSLL (D × 24%)</div>
                      <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.tributosPresumidos)}</div>
                    </div>
                  </div>
                </div>
              )}

              {(item.status === 'ERRO' || item.status === 'ALERTA') && (
                <div className='text-muted fs-9 mb-2'>
                  Operações irregulares: {formatCurrency(item.operacoesIrregularesEcd)} × 5% = {formatCurrency(item.multaBruta5Ecd)}
                  {' | '}Receita Bruta do Período: {item.receitaBrutaPeriodoEcd != null ? formatCurrency(item.receitaBrutaPeriodoEcd) : 'não disponível'}
                  {item.tetoLegal1Ecd != null && <> × 1% = {formatCurrency(item.tetoLegal1Ecd)}</>}
                  {' | '}Critério aplicado: {item.criterioAplicadoEcd ?? item.statusEvidenciaEcd}
                </div>
              )}

              <div className={`alert p-3 fs-8 ${
                item.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
                item.status === 'ALERTA' ? 'alert-light-warning border border-warning' :
                                           'alert-light-info border border-info'
              }`}>
                {item.mensagemDiagnostico}
              </div>

              {item.mensagemDirecionamento && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mt-2'>
                  <strong>Direcionamento:</strong> {item.mensagemDirecionamento}
                </div>
              )}

              <BlocoLancamentosRelacionados item={item} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaAssetRecoveryDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Auditoria de Reversão de Perdas (Asset Recovery)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) ·{' '}
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s) · {resultado.totalAvisos} aviso(s)
            {resultado.passivoTotal > 0 && (
              <> · Passivo:{' '}
                <strong className='text-danger'>
                  {resultado.passivoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </strong>
              </>
            )}
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhuma reversão de perdas sem contrapartida detectada. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Mês/Ano</th>
                  <th className='text-end'>Créd. Baixa Est.</th>
                  <th className='text-end'>Créd. PDD</th>
                  <th className='text-end'>Total Créd. Desp.</th>
                  <th className='text-end'>Rec. Reversão</th>
                  <th className='text-end'>Divergência</th>
                  <th className='text-end'>Passivo Presumido</th>
                  <th className='text-center'>Ocorrência</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaAssetRecoveryDetalhe;
